import {
  Briefcase,
  Building2,
  CopyCheck,
  Handshake,
  LayoutDashboard,
  type LucideIcon,
  PanelLeft,
  UserRound,
  Workflow
} from 'lucide-react'
import { useLocation } from 'react-router'
import { DataTableSkeleton } from '@/shared/components/data-table/data-table-skeleton'
import { FEATURES } from '@/shared/config/features'
import { readStoredSidebarOpen } from '@/shared/lib/sidebar-storage'
import { cn } from '@/shared/lib/utils'
import { Skeleton } from '@/shared/ui/skeleton'

type LoadingContentKind = 'dashboard' | 'business' | 'data-table' | 'content'

type LoadingNavigationItem = {
  segment: string
  label: string
  icon: LucideIcon
}

const NAVIGATION_ROWS: LoadingNavigationItem[] = [
  { segment: 'dashboard', label: 'Inicio', icon: LayoutDashboard },
  { segment: 'clients', label: 'Clientes', icon: UserRound },
  { segment: 'negotiations', label: 'Negociaciones', icon: Handshake },
  { segment: 'deliveries', label: 'Postventa', icon: CopyCheck },
  { segment: 'inventory', label: 'Inventario', icon: Briefcase },
  // Mismo flag que el ítem real: si no, el esqueleto pinta una fila que después desaparece.
  ...(FEATURES.clientPortal ? [{ segment: 'flows', label: 'Flujos', icon: Workflow }] : []),
  { segment: 'business', label: 'Empresa', icon: Building2 }
]
const DASHBOARD_STATE_ROWS = ['state-1', 'state-2', 'state-3', 'state-4', 'state-5', 'state-6']
const DASHBOARD_LIST_ROWS = ['recent-1', 'recent-2', 'recent-3']
const KPI_DATA_TABLE_SEGMENTS = new Set(['clients', 'inventory', 'negotiations'])

function resolveLoadingRoute(pathname: string): {
  activeSegment: string | null
  content: LoadingContentKind
  label: string
} {
  const segments = pathname.split('/').filter(Boolean)
  const adminIndex = segments.indexOf('admin')
  const activeSegment = adminIndex >= 0 ? (segments[adminIndex + 1] ?? 'dashboard') : null
  const navigationItem = NAVIGATION_ROWS.find((item) => item.segment === activeSegment)

  if (navigationItem) {
    return {
      activeSegment,
      content:
        activeSegment === 'dashboard'
          ? 'dashboard'
          : activeSegment === 'business'
            ? 'business'
            : KPI_DATA_TABLE_SEGMENTS.has(activeSegment ?? '')
              ? 'data-table'
              : 'content',
      label: navigationItem.label
    }
  }

  const labels: Record<string, string> = {
    account: 'Mi perfil',
    business: 'Empresa',
    changelog: 'Changelog',
    revalidation: 'Revalidación'
  }
  const fallbackSegment = activeSegment ?? segments[0] ?? ''

  return {
    activeSegment,
    content: 'content',
    label: labels[fallbackSegment] ?? 'IDC Forms'
  }
}

function SkeletonBlock({ className }: { className?: string }) {
  return <Skeleton aria-hidden="true" className={cn('motion-reduce:animate-none', className)} />
}

function DashboardListCard({ titleWidth }: { titleWidth: string }) {
  return (
    <div className="min-w-0 overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm">
      <div className="flex items-center justify-between p-6">
        <div className="flex items-center gap-2">
          <SkeletonBlock className="size-5 rounded" />
          <SkeletonBlock className={cn('h-5', titleWidth)} />
        </div>
        <SkeletonBlock className="h-4 w-20" />
      </div>
      <div className="px-6 pb-6">
        <div className="space-y-1">
          {DASHBOARD_LIST_ROWS.map((row, index) => (
            <div key={row} className="border-b border-b-muted p-2 last:border-b-0">
              <div className="mb-2 flex items-center gap-2">
                <SkeletonBlock className={cn('h-4', index === 1 ? 'w-24' : 'w-16')} />
                <SkeletonBlock className="h-3 w-32" />
                <SkeletonBlock className="ml-auto h-4 w-14 rounded-full" />
              </div>
              <div className="flex items-center gap-4">
                <SkeletonBlock className="h-3 w-24" />
                <SkeletonBlock className="h-3 w-20" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function DashboardLoadingContent() {
  return (
    <div className="space-y-8 px-6 py-6 lg:px-14 lg:py-9">
      <div className="mb-8 space-y-2">
        <SkeletonBlock className="h-9 w-64 max-w-[80vw]" />
        <SkeletonBlock className="h-5 w-80 max-w-[85vw]" />
      </div>

      <div className="grid min-w-0 gap-6 lg:grid-cols-2">
        <div>
          <SkeletonBlock className="mb-3 h-5 w-48" />
          <div className="grid min-w-0 gap-2 md:grid-cols-2 xl:grid-cols-3">
            {DASHBOARD_STATE_ROWS.map((state, index) => (
              <div key={state} className="rounded-xl border bg-card p-2 shadow-sm">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <SkeletonBlock className="size-2 shrink-0 rounded-full" />
                    <SkeletonBlock className={cn('h-3', index % 2 === 0 ? 'w-20' : 'w-24')} />
                  </div>
                  <SkeletonBlock className="h-6 w-8" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid h-fit gap-3 md:grid-cols-2 xl:grid-cols-3">
          <SkeletonBlock className="col-span-full h-5 w-20" />
          {['metric-1', 'metric-2', 'metric-3'].map((metric) => (
            <div key={metric} className="rounded-xl border bg-card p-3 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="space-y-2">
                  <SkeletonBlock className="h-7 w-12" />
                  <SkeletonBlock className="h-3 w-28" />
                </div>
                <SkeletonBlock className="size-5 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid min-w-0 gap-6 lg:grid-cols-2">
        <DashboardListCard titleWidth="w-44" />
        <DashboardListCard titleWidth="w-36" />
      </div>
    </div>
  )
}

function ListKpisLoading() {
  return (
    <div className="w-full overflow-hidden px-3 pb-2">
      <div className="grid min-w-[944px] grid-cols-4 gap-2">
        {['kpi-1', 'kpi-2', 'kpi-3'].map((kpi) => (
          <div key={kpi} className="min-h-28 rounded-md bg-secondary px-4 py-3 dark:bg-accent">
            <div className="mb-3 flex items-center gap-2">
              <SkeletonBlock className="size-6 rounded-sm bg-accent dark:bg-secondary" />
              <SkeletonBlock className="h-4 w-28 rounded-sm bg-accent dark:bg-secondary" />
            </div>
            <SkeletonBlock className="h-7 w-16 rounded-sm bg-accent dark:bg-secondary" />
            <SkeletonBlock className="mt-2 h-3 w-36 rounded-sm bg-accent dark:bg-secondary" />
          </div>
        ))}
      </div>
    </div>
  )
}

function QueryFilterLoading() {
  return (
    <div className="flex min-h-10 w-full items-center gap-1 rounded-md border border-input bg-background px-1">
      <SkeletonBlock className="h-8 w-20 shrink-0 bg-secondary dark:bg-accent" />
      <span className="mx-1 h-5 w-px shrink-0 bg-border" />
      <SkeletonBlock className="h-7 w-56 shrink-0 bg-secondary dark:bg-accent" />
      <SkeletonBlock className="h-7 w-44 shrink-0 bg-secondary dark:bg-accent" />
      <SkeletonBlock className="ml-1 h-4 min-w-40 flex-1 bg-secondary dark:bg-accent" />
    </div>
  )
}

function ListLoadingContent({ showKpis = false }: { showKpis?: boolean }) {
  return (
    <div className="flex h-full min-h-0 flex-col pt-3">
      {showKpis ? <ListKpisLoading /> : null}

      <div className="shrink-0 pl-3">
        <div className="flex h-10 items-center gap-2 overflow-hidden py-1.5 pr-2">
          {['tab-1', 'tab-2', 'tab-3', 'tab-4'].map((tab, index) => (
            <SkeletonBlock
              key={tab}
              className={cn('h-7 rounded-md', index === 0 ? 'w-28' : 'w-24')}
            />
          ))}
        </div>

        <div className="border-b border-border pb-1.5 pr-2">
          <QueryFilterLoading />
        </div>

        <div className="flex h-10 items-center justify-between gap-3 border-b border-border py-1.5 pr-1">
          <div className="flex min-w-0 flex-1 gap-1.5 overflow-hidden">
            <SkeletonBlock className="h-6 w-20 rounded-full" />
            <SkeletonBlock className="h-6 w-24 rounded-full" />
            <SkeletonBlock className="hidden h-6 w-20 rounded-full sm:block" />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <SkeletonBlock className="h-4 w-36" />
            <SkeletonBlock className="size-7" />
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        <DataTableSkeleton columns={8} rows={18} fillHeight flush />
      </div>

      <div className="ml-3 flex h-10 shrink-0 items-center justify-between border-t pr-3">
        <div className="flex items-center gap-2">
          <SkeletonBlock className="size-6" />
          <SkeletonBlock className="size-6" />
          <SkeletonBlock className="h-3 w-20" />
        </div>
        <SkeletonBlock className="h-3 w-28" />
      </div>
    </div>
  )
}

function BusinessLoadingContent() {
  return (
    <div className="flex h-full min-h-0 bg-neutral-50 dark:bg-background">
      <div className="hidden w-60 shrink-0 border-r border-border bg-background p-3 lg:block">
        <SkeletonBlock className="mb-4 h-6 w-24" />
        <div className="space-y-1.5">
          {Array.from({ length: 10 }, (_, index) => `business-nav-${index}`).map((item, index) => (
            <div key={item} className="flex h-8 items-center gap-2 rounded-md px-2">
              <SkeletonBlock className="size-4 shrink-0" />
              <SkeletonBlock className={cn('h-4', index % 3 === 0 ? 'w-28' : 'w-36')} />
            </div>
          ))}
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <ListLoadingContent />
      </div>
    </div>
  )
}

type PageLoadingSkeletonProps = {
  className?: string
}

export function PageLoadingSkeleton({ className }: PageLoadingSkeletonProps) {
  return (
    <section
      aria-busy="true"
      aria-label="Cargando contenido"
      className={cn('h-full min-h-0 w-full bg-background', className)}
    >
      <span className="sr-only" role="status">
        Cargando contenido
      </span>
      <ListLoadingContent />
    </section>
  )
}

export function DataTablePageLoadingSkeleton({ className }: PageLoadingSkeletonProps) {
  return (
    <section
      aria-busy="true"
      aria-label="Cargando listado"
      className={cn('h-full min-h-0 w-full bg-background', className)}
    >
      <span className="sr-only" role="status">
        Cargando listado
      </span>
      <ListLoadingContent showKpis />
    </section>
  )
}

export function BusinessPageLoadingSkeleton({ className }: { className?: string }) {
  return (
    <section
      aria-busy="true"
      aria-label="Cargando Empresa"
      className={cn('h-full min-h-0 w-full', className)}
    >
      <BusinessLoadingContent />
    </section>
  )
}

export function BusinessOutletLoadingSkeleton({ className }: { className?: string }) {
  return <PageLoadingSkeleton className={className} />
}

export function AdminRouteLoadingSkeleton() {
  const { pathname } = useLocation()
  const { content } = resolveLoadingRoute(pathname)

  if (content === 'business') return <BusinessPageLoadingSkeleton />
  if (content === 'dashboard') return <DashboardLoadingContent />
  if (content === 'data-table') return <DataTablePageLoadingSkeleton />
  return <PageLoadingSkeleton />
}

export function AppLoadingShell({ pathname = '/admin/dashboard' }: { pathname?: string }) {
  const loadingRoute = resolveLoadingRoute(pathname)
  const sidebarOpen = readStoredSidebarOpen()

  return (
    <div aria-busy="true" className="flex h-svh w-full overflow-hidden bg-sidebar text-foreground">
      <span className="sr-only" role="status">
        Cargando IDC Forms
      </span>

      <aside
        className={cn(
          'hidden h-full shrink-0 flex-col bg-sidebar md:flex',
          sidebarOpen ? 'w-64' : 'w-12'
        )}
      >
        <div className="flex flex-col gap-2 p-2">
          <div className={cn('flex', !sidebarOpen && 'justify-center')}>
            <div className={cn('shrink-0 rounded-md bg-white p-1', !sidebarOpen && 'size-8')}>
              <img src="/images/logo.svg" className="h-auto w-[34px]" alt="" />
            </div>
            {sidebarOpen ? (
              <div className="flex flex-col justify-center whitespace-nowrap pl-2">
                <p className="text-sm font-bold text-primary dark:text-white">IDC Forms</p>
                <p className="text-xs text-muted-foreground">Real Estate</p>
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
          <div className="relative flex w-full min-w-0 flex-col p-2">
            {sidebarOpen ? (
              <div className="flex h-8 shrink-0 items-center px-2 text-xs font-medium text-sidebar-foreground/50">
                Plataforma
              </div>
            ) : null}
            <div className="flex w-full min-w-0 flex-col gap-1">
              {NAVIGATION_ROWS.map((item, index) => {
                const isActive = item.segment === loadingRoute.activeSegment
                if (isActive) {
                  return (
                    <div
                      key={item.segment}
                      className={cn(
                        'flex h-8 items-center gap-2 rounded-md bg-sidebar-primary p-2 text-sm text-sidebar-primary-foreground',
                        !sidebarOpen && 'w-8 justify-center'
                      )}
                    >
                      <item.icon className="size-4 shrink-0" aria-hidden="true" />
                      {sidebarOpen ? <span>{item.label}</span> : null}
                    </div>
                  )
                }

                return (
                  <div
                    key={item.segment}
                    className={cn(
                      'flex h-8 items-center gap-2 rounded-md px-2',
                      !sidebarOpen && 'w-8 justify-center'
                    )}
                  >
                    <SkeletonBlock className="size-4 shrink-0 rounded bg-sidebar-foreground/10" />
                    {sidebarOpen ? (
                      <SkeletonBlock
                        className={cn(
                          'h-4 bg-sidebar-foreground/10',
                          index % 3 === 0 ? 'w-20' : 'w-28'
                        )}
                      />
                    ) : null}
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 p-2">
          <div
            className={cn(
              'flex h-12 items-center gap-2 rounded-md p-2',
              !sidebarOpen && 'justify-center px-0'
            )}
          >
            <SkeletonBlock className="size-8 shrink-0 rounded-lg bg-sidebar-foreground/10" />
            {sidebarOpen ? (
              <>
                <div className="flex-1 space-y-2">
                  <SkeletonBlock className="h-3.5 w-24 bg-sidebar-foreground/10" />
                  <SkeletonBlock className="h-3 w-32 bg-sidebar-foreground/10" />
                </div>
                <SkeletonBlock className="size-4 shrink-0 bg-sidebar-foreground/10" />
              </>
            ) : null}
          </div>
        </div>
      </aside>

      <main className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-3 px-3 py-2 sm:h-16">
          <div className="flex size-8 items-center justify-center rounded-md text-foreground">
            <PanelLeft className="size-4" aria-hidden="true" />
          </div>
          <div className="h-4 w-px bg-border" />
          <span className="text-sm font-normal text-foreground">{loadingRoute.label}</span>
          <div className="ml-auto flex gap-2">
            <SkeletonBlock className="hidden h-8 w-24 sm:block" />
            <SkeletonBlock className="size-8" />
          </div>
        </header>

        <div className="min-h-0 flex-1 px-2 pb-2 pt-1">
          <div
            className={cn(
              'h-full rounded-xl border border-accent bg-background',
              loadingRoute.content === 'dashboard' ? 'overflow-auto' : 'overflow-hidden'
            )}
          >
            {loadingRoute.content === 'dashboard' ? <DashboardLoadingContent /> : null}
            {loadingRoute.content === 'business' ? <BusinessLoadingContent /> : null}
            {loadingRoute.content === 'data-table' ? <ListLoadingContent showKpis /> : null}
            {loadingRoute.content === 'content' ? <ListLoadingContent /> : null}
          </div>
        </div>
      </main>
    </div>
  )
}
